import React, { useState } from "react";
import { List, Card, Avatar, Button, Icon, Tooltip } from "antd";
import sanitizeHtml from "sanitize-html";
import { injectIntl, FormattedMessage } from "react-intl";

import { waitFor } from "../../utils/util";

import chatService from "../../services/chat";

import DatetimeFormat from "../DateTimeFormat";

import DefaultUserImage from "../../assets/img/avatar_user_black.png";
import "./MessageItem.scss";

const { Meta } = Card;
let moment = require("moment");

const formatDate = (dateToFormat) => {
  let formattedDate = dateToFormat;
  if (isNaN(formattedDate)) {
    let date = formattedDate.split(" ")[0];
    let time = formattedDate.split(" ")[1];

    date = date.split("-");
    time = time.split(":");

    formattedDate =
      date[1] +
      "/" +
      date[2] +
      "/" +
      date[0] +
      "/" +
      time[0] +
      ":" +
      time[1] +
      ":" +
      time[2];
  }

  return formattedDate;
};

const canPostMessageAvatar = (item, previousItem) => {
  if (previousItem?.from?.username === item?.from?.username) {
    if (
      moment(new Date(formatDate(item.sendDate))).diff(
        moment(new Date(formatDate(previousItem.sendDate))),
        "minutes"
      ) < 1
    ) {
      return false;
    }
  }

  return true;
};

const canPostDifferentDaySpan = (item, previousItem, index) => {
  if (index === 0 || !previousItem) {
    return true;
  }

  return (
    moment(new Date(formatDate(previousItem.sendDate)))
      .startOf("day")
      .diff(
        moment(new Date(formatDate(item.sendDate))).startOf("day"),
        "days"
      ) !== 0
  );
};

const Title = ({ item }) => {
  return (
    <div className="message-list-header">
      <span className="message-list-title">
        {item.from && item.from.username}
      </span>

      <span className="bullet">&#8226;</span>

      <DatetimeFormat className="time" time dateTime={item.sendDate} />
    </div>
  );
};

const ItemDescription = ({ text }) => {
  let localText = sanitizeHtml(text, { allowedTags: [] });

  localText = localText.replace(
    /\B(@[a-zA-Z\u00C0-\u017F0-9._-]+)/g,
    (match) => {
      return `<span style="color: #1976D2">${match}</span>`;
    }
  );

  return <span dangerouslySetInnerHTML={{ __html: localText }} />;
};

const getAttachmentExtension = (fileName) => {
  if (!fileName || fileName.indexOf(".") === -1) {
    return "";
  }

  return fileName.split(".").pop().toUpperCase();
};

const AttachmentDescription = ({ item }) => {
  const [viewing, setViewing] = useState(false);
  const [isViewedError, setIsViewedError] = useState(false);
  const [viewFileTooltipVisible, setViewFileTooltipVisible] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [isDownloadingError, setIsDownloadingError] = useState(false);

  const fileName = item?.content || "";
  const extension = getAttachmentExtension(fileName);

  const handleDownload = async () => {
    setIsDownloadingError(false);
    setIsViewedError(false);

    setDownloading(true);

    try {
      await chatService.downloadAttachment(item.messageId, fileName);
    } catch (error) {
      setIsDownloadingError(true);
    } finally {
      setDownloading(false);
    }
  };

  const handleView = async () => {
    setIsDownloadingError(false);
    setIsViewedError(false);

    setViewing(true);
    setViewFileTooltipVisible(false);

    try {
      await chatService.viewAttachment(item.messageId);
    } catch (error) {
      setIsViewedError(true);
    } finally {
      await waitFor(250);
      setViewing(false);
    }
  };

  return (
    <div className="message-attachment">
      <div
        className={`message-attachment-main ${
          isViewedError || isDownloadingError ? "error" : ""
        }`}
      >
        <div className="message-attachment-content">
          <span className="message-attachment-name">{fileName}</span>

          <div className="message-attachment-actions">
            <Tooltip
              title={<FormattedMessage id="chat.viewFile" />}
              overlayClassName="social-tooltip"
              visible={viewFileTooltipVisible && !viewing}
              onVisibleChange={(visible) => {
                if (viewing) {
                  return false;
                }

                setViewFileTooltipVisible(visible);
              }}
            >
              <Button
                type="link"
                icon="eye"
                shape="circle"
                className="message-attachment-action"
                loading={viewing}
                disabled={viewing}
                onClick={handleView}
              />
            </Tooltip>

            <Tooltip
              title={<FormattedMessage id="chat.downloadFile" />}
              overlayClassName="social-tooltip"
            >
              <Button
                type="link"
                icon="download"
                shape="circle"
                className="message-attachment-action"
                loading={downloading}
                disabled={downloading}
                onClick={handleDownload}
              />
            </Tooltip>
          </div>
        </div>

        {(isViewedError || isDownloadingError) && (
          <div className="message-attachment-error">
            <Icon type="exclamation-circle" />
            {isViewedError && <FormattedMessage id="chat.viewError" />}
            {isDownloadingError && <FormattedMessage id="chat.downloadError" />}
          </div>
        )}
      </div>

      {extension && <div className="message-attachment-meta">{extension}</div>}
    </div>
  );
};

const MessageItem = ({ item, previousItem, index, intl }) => {
  const description = item?.isAttachment ? (
    <AttachmentDescription item={item} />
  ) : (
    <ItemDescription text={item.content} />
  );

  const showDate = canPostDifferentDaySpan(item, previousItem, index);
  const showAvatar = canPostMessageAvatar(item, previousItem);

  if (showAvatar) {
    return (
      <List.Item>
        {showDate && (
          <DatetimeFormat className="date-time" long dateTime={item.sendDate} />
        )}

        <Card bordered={false} className="single-message">
          <Meta
            avatar={<Avatar src={DefaultUserImage} />}
            title={<Title item={item} />}
            description={description}
          />
        </Card>
      </List.Item>
    );
  }

  return (
    <List.Item className="hidden-avatar">
      {showDate && (
        <DatetimeFormat className="date-time" long dateTime={item.sendDate} />
      )}

      <Card bordered={false} className="single-message">
        <Meta description={description} />
      </Card>
    </List.Item>
  );
};

export default injectIntl(MessageItem);
